import { useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import { getDateWithDayToDisplay } from "../../../../../utils/date-helpers";
import SvgIcon from "../../../../../parts/SvgIcon";

export const DuplicateMealPlanModal = ({ locals }) => {
  const { query, mealPlan } = locals;
  const { date, athlete, mealPlan: mealPlanId, week = 1 } = query;
  const { weeks = [] } = mealPlan;
  const meals = weeks[0]?.days[0]?.meals?.map(({ type }) => type) || [];
  const dateToUse = athlete ? date : `Week ${week} ${date}`;
  const initialState = {
    selectedWeekIndex: 0,
    selectedDaysByWeek: {},
    selectedMeals: [],
  };
  const [state, setState] = useState(initialState);
  const { selectedWeekIndex, selectedDaysByWeek, selectedMeals } = state;
  const hasSelectedMeals = selectedMeals.length > 0;

  const hasSelectedDays = Object.values(selectedDaysByWeek).some(
    (days) => days.length > 0
  );

  const canDuplicate = hasSelectedMeals && hasSelectedDays;

  /**
   * Function to select the meals
   * @param {*} mealType
   */
  const toggleMealSelection = (mealType) => {
    setState((prev) => {
      const isSelected = prev.selectedMeals.includes(mealType);
      return {
        ...prev,
        selectedMeals: isSelected
          ? prev.selectedMeals.filter((m) => m !== mealType)
          : [...prev.selectedMeals, mealType],
      };
    });
  };

  /**
   * Function to select the week
   * @param {*} index
   */
  const handleWeekSelect = (index) => {
    setState((prev) => ({
      ...prev,
      selectedWeekIndex: index,
    }));
  };

  /**
   * Function to select the days
   * @param {*} weekIndex
   * @param {*} dayIndex
   */
  const toggleDaySelection = (weekIndex, dayIndex) => {
    setState((prev) => {
      const weekDays = prev.selectedDaysByWeek[weekIndex] || [];
      const isSelected = weekDays.includes(dayIndex);

      return {
        ...prev,
        selectedDaysByWeek: {
          ...prev.selectedDaysByWeek,
          [weekIndex]: isSelected
            ? weekDays.filter((i) => i !== dayIndex)
            : [...weekDays, dayIndex],
        },
      };
    });
  };

  /**
   * Duplicate action
   */
  const handleDuplicate = () => {
    const body = {
      initDate: dateToUse,
      isTemplate: !athlete,
      meals: selectedMeals,
      selectedDaysByWeek,
    };
    submit(
      `/actions/meal-planner/duplicate-day-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body,
      }
    );
  };

  /**
   * Reset the state
   */
  const handleClearAll = () => {
    setState(initialState);
  };

  return (
    <Modal
      className="duplicate-meal-plan-modal"
      title={`Duplicate ${getDateWithDayToDisplay(dateToUse)} meals`}
      toClear={["date"]}
    >
      <div className="body">
        <div className="meals-title">
          <span>Meals</span>
        </div>
        <div className="select-meals">
          <ul>
            {meals.map((item, index) => (
              <li key={`${item}-${index}`}>
                <button type="button" onClick={() => toggleMealSelection(item)}>
                  <SvgIcon
                    src={`/img/new-icons/${
                      selectedMeals.includes(item)
                        ? "checked-square.svg"
                        : "unchecked-square.svg"
                    }`}
                    noFilterOnLight={selectedMeals.includes(item)}
                  />
                  <span>{item}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="select-weeks">
          <span className="clipped font-bold">Duplicate To</span>
          <ul className="buttons">
            {weeks.map((week, index) => (
              <li key={week.rangeWeek?.start || index}>
                <button
                  onClick={() => handleWeekSelect(index)}
                  data-button-type={
                    selectedWeekIndex === index ? "primary" : "secondary"
                  }
                >
                  Week {index + 1}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="dates">
          <ul>
            {weeks[selectedWeekIndex]?.days
              .reduce((acc, { date }, idx) => {
                const shouldInclude = date
                  ? date !== dateToUse
                  : `Week ${selectedWeekIndex + 1} Day ${idx + 1}` !==
                    dateToUse;

                if (shouldInclude) {
                  acc.push({ date, idx });
                }
                return acc;
              }, [])
              .map(({ date, idx }) => {
                const label = date
                  ? getDateWithDayToDisplay(date)
                  : `Day ${idx + 1}`;

                const isSelected =
                  selectedDaysByWeek[selectedWeekIndex]?.includes(idx);

                return (
                  <li key={`${date}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => toggleDaySelection(selectedWeekIndex, idx)}
                    >
                      <SvgIcon
                        src={`/img/new-icons/${
                          isSelected
                            ? "checked-square.svg"
                            : "unchecked-square.svg"
                        }`}
                        noFilterOnLight={isSelected}
                      />
                      <span className={!isSelected ? "opacity-70" : ""}>
                        {label}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className="footer">
          <button
            data-button-type="secondary"
            data-action="cancel"
            onClick={handleClearAll}
          >
            Clear All
          </button>
          <button
            type="submit"
            data-button-type="primary"
            disabled={!canDuplicate}
            onClick={handleDuplicate}
          >
            Duplicate
          </button>
        </div>
      </div>
    </Modal>
  );
};

DuplicateMealPlanModal.propTypes = {
  locals: PropTypes.object,
};
